import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import logger from "@insite/client-framework/Logger";
import { getPageByUrl, getPagesByParent, PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageProps, { PageModel } from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const newsAndListPageUrl = getPageLinkByPageType(state, "NewsListPage")?.type as string;
    const nodeId: string = state.links.pageTypesToNodeId[newsAndListPageUrl];
    return {
        parentNodeId: nodeId,
        propsState: state,
    };
};

const enum fields {
    heading = "heading",
    subheading = "subheading",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.subheading]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
export interface PopularHerbsStyles {
    blogContainer?: InjectableCss;
    blogSlide?: InjectableCss;
    blogWithoutSlide?: InjectableCss;
    blogImage?: LazyImageProps;
    blogImageWithoutCarousal?: LazyImageProps;
    blogTitle?: TypographyProps;
    prevArrowContainer?: InjectableCss;
    prevArrowButton?: ButtonProps;
    nextArrowContainer?: InjectableCss;
    nextArrowButton?: ButtonProps;
    blogListWrapper?: InjectableCss;
    blogListWrapperContainer?: InjectableCss;
    blogListWrapperWithCarousal?: InjectableCss;
    blogListWrapperWithoutCarousal?: InjectableCss;
    popularHerbsWrapper?: InjectableCss;
    popularHerbsHeading?: TypographyProps;
    popularHerbsSubHeading?: TypographyProps;
    noResultFound?: TypographyProps;
}
const styles: PopularHerbsStyles = {
    popularHerbsWrapper: {
        css: css`
            text-align: center;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            background: #f0f4f3;
            padding: 50px 0 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px;
                    `,
                    css`
                        padding: 20px 0px;
                    `,
                    css`
                        padding: 20px 0px;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    popularHerbsHeading: {
        variant: "h2",
        size: 22,
        color: "primary",
        css: css`
            margin-bottom: 20px;
        `,
    },
    popularHerbsSubHeading: {
        css: css`
            margin-bottom: 30px;
            width: 60%;
            display: block;
            padding: 0 100px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 20px;
                        width: 100%;
                    `,
                    css`
                        padding: 0 20px;
                        width: 100%;
                    `,
                    css`
                        padding: 0;
                        width: 80%;
                    `,
                    css`
                        padding: 0;
                    `,
                    null,
                ])}
        `,
    },
    blogContainer: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
    blogSlide: {
        css: css`
            position: relative;
            flex: 0 0 13%;
            align-items: flex-start;
            display: flex;
            flex-direction: column;
            margin-right: 20px;
            &:focus {
                border-radius: 10px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 0 0 38%;
                    `,
                    css`
                        flex: 0 0 38%;
                    `,
                    css`
                        flex: 0 0 31.4%;
                    `,
                    css`
                        flex: 0 0 23.2%;
                    `,
                    css`
                        flex: 0 0 12.9%;
                    `,
                ])}
        `,
    },
    blogWithoutSlide: {
        css: css`
            width: 162px;
            align-items: flex-start;
            display: flex;
            flex-direction: column;
            margin-right: 20px;
            &:last-child {
                margin-right: 0;
            }
            @media only screen and (min-width: 1200px) and (max-width: 1260px) {
                width: 140px;
            }
            &:focus {
                outline: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 162px;
                        align-items: space-between;
                    `,
                    css`
                        width: 162px;
                        align-items: space-between;
                    `,
                    css`
                        width: 165px;
                    `,
                    css`
                        width: 165px;
                    `,
                    null,
                ])}
        `,
    },
    blogImage: {
        css: css`
            width: 100%;
            height: 162px;
            border-radius: 10px;
            img {
                height: 100%;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 162px;
                        height: 162px;
                    `,
                    css`
                        width: 200px;
                        height: 200px;
                    `,
                    css`
                        height: 214px;
                    `,
                    css`
                        height: 209px;
                    `,
                    null,
                ])}
        `,
    },
    blogImageWithoutCarousal: {
        css: css`
            width: 100%;
            height: 162px;
            border-radius: 10px;
            img {
                height: 100%;
            }
            @media only screen and (min-width: 1200px) and (max-width: 1260px) {
                height: 140px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: 162px;
                    `,
                    css`
                        height: 162px;
                    `,
                    css`
                        height: 165px;
                    `,
                    css`
                        height: 165px;
                    `,
                    null,
                ])}
        `,
    },

    blogTitle: {
        variant: "h3",
        color: "#183028",
        size: 20,
        css: css`
            margin-top: 7px;
            font-weight: unset;
            font-family: LibreBaskerville;
            text-transform: capitalize;
            text-align: left;
            margin-bottom: 0;
        `,
    },

    nextArrowContainer: {
        css: css`
            justify-content: flex-end;
            position: absolute;
            right: 0;
            top: 30%;
            width: 40px;
            height: 40px;
            overflow: hidden;
            border-radius: 50%;
            border: 1px solid #e5e5e5;
            z-index: 2;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            background: #fff;
            span {
                width: 30px;
                height: 30px;
                padding-left: 3px;
                top: 0 !important;
                > svg {
                    color: #d14124;
                    width: 25px;
                    height: 25px;
                }
            }
            &:hover {
                background: #d14124;
                svg {
                    color: #fff;
                }
            }
        `,
    },
    prevArrowContainer: {
        css: css`
            justify-content: flex-start;
            position: absolute;
            left: 0px;
            top: 30%;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: 1px solid #e5e5e5;
            overflow: hidden;
            z-index: 2;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    null,
                    null,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            background: #fff;
            span {
                width: 30px;
                height: 30px;
                top: 0 !important;
                > svg {
                    color: #d14124;
                    width: 25px;
                    height: 25px;
                }
            }
            &:hover {
                background: #d14124;
                svg {
                    color: #fff;
                }
            }
        `,
    },
    blogListWrapper: {
        css: css`
            width: 100%;
            position: relative;
            padding: 0 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px;
                    `,
                    css`
                        padding: 0px;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    blogListWrapperContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    blogListWrapperWithCarousal: {
        css: css`
            display: flex;
            width: 100%;
            padding-top: 2px;
            padding-bottom: 2px;
        `,
    },
    blogListWrapperWithoutCarousal: {
        css: css`
            display: flex;
            justify-content: center;
            width: 100%;
            padding-top: 2px;
            padding-bottom: 2px;
        `,
    },
    noResultFound: {
        variant: "p",
        css: css`
            font-size: 16px;
            font-weight: 700;
            align-items: center;
            height: 64px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
};

const PopularHerbs: React.FC<Props> = ({ propsState, fields }) => {
    const [pages, setPages] = React.useState<PageModel[] | undefined>([]);
    React.useEffect(() => {
        getPagesAndLinks();
    }, []);
    const getPagesAndLinks = async () => {
        try {
            const pageByUrl = await getPageByUrl("/HerbsReferenceGuide");
            if (pageByUrl && pageByUrl.page) {
                const childPages = await getPagesByParent({ parentNodeId: pageByUrl.page.nodeId });
                if (childPages.pages) {
                    setPages(
                        childPages.pages.filter(val => Object.values(val.translatableFields.popularHerb)[0] === true),
                    );
                }
            }
        } catch (err) {
            logger.error(err);
        }
    };

    let isCarousal = false;
    if (typeof window !== "undefined") {
        if (window.screen.width > 1200 && (pages?.length as number) > 7) {
            isCarousal = true;
        } else if ((pages?.length as number) > 4 && window.screen.width < 1200 && window.screen.width > 767) {
            isCarousal = true;
        } else if (window.screen.width < 768 && (pages?.length as number) > 3) {
            isCarousal = true;
        } else if (window.screen.width < 576 && (pages?.length as number) > 1) {
            isCarousal = true;
        } else {
            isCarousal = false;
        }
    }

    const [emblaRef, embla] = useEmblaCarousel();
    const [, setCanScrollPrev] = React.useState(false);
    const [, setCanScrollNext] = React.useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        embla.reInit({
            align: "start",
            // slidesToScroll: 1,
            loop: true,
        });
        setCanScroll();
    }, [embla]);

    return (
        <StyledWrapper {...styles.popularHerbsWrapper}>
            <Typography {...styles.popularHerbsHeading}>{fields.heading}</Typography>
            <Typography {...styles.popularHerbsSubHeading}>{parse(fields.subheading, parserOptions)}</Typography>
            {pages?.length !== 0 ? (
                <StyledWrapper {...styles.blogContainer}>
                    {isCarousal && (
                        <StyledWrapper {...styles.prevArrowContainer}>
                            <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                                <ButtonIcon src={ChevronLeft} />
                            </Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.blogListWrapper}>
                        <StyledWrapper {...styles.blogListWrapperContainer} ref={isCarousal ? emblaRef : null}>
                            <StyledWrapper
                                {...(isCarousal
                                    ? { ...styles.blogListWrapperWithCarousal }
                                    : { ...styles.blogListWrapperWithoutCarousal })}
                            >
                                {pages?.map((page: PageProps) => (
                                    <Clickable
                                        href={`/HerbsReferenceGuide/${
                                            Object.values(page.translatableFields["urlSegment"])[0]
                                        }`}
                                        key={page.id}
                                        {...(isCarousal ? { ...styles.blogSlide } : { ...styles.blogWithoutSlide })}
                                    >
                                        <LazyImage
                                            src={Object.values(page.translatableFields["herbFeaturedImage"])[0]}
                                            {...(isCarousal
                                                ? { ...styles.blogImage }
                                                : { ...styles.blogImageWithoutCarousal })}
                                        />
                                        <Typography {...styles.blogTitle}>
                                            {Object.values(page.translatableFields["herbName"])[0]}
                                        </Typography>
                                    </Clickable>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    {isCarousal && (
                        <StyledWrapper {...styles.nextArrowContainer}>
                            <Button {...styles.nextArrowButton} onClick={() => embla && embla.scrollNext()}>
                                <ButtonIcon src={ChevronRight} />
                            </Button>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            ) : (
                <Typography {...styles.noResultFound}>
                    {translate(" We're sorry, there were no result found for Popular Herbs.")}
                </Typography>
            )}
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PopularHerbs),
    definition: {
        group: "Basic",
        displayName: "PopularHerbs",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Popular Herbs",
                fieldType: "Translatable",
            },
            {
                name: fields.subheading,
                displayName: "subTitle",
                editorTemplate: "RichTextField",
                defaultValue:
                    "<p>Our Herbal Reference Guide lets you enhance your relationship with herbs by giving you a comprehensive profile of each plant.</p>",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
